/**
 * Module dependencies.
 */

import type { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import type {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { formatCurrency } from 'app/core/utils/formatter';
import { useCrudRequest } from 'app/hooks/requests/crud/use-crud-request';
import { useTransactionBadges } from 'app/hooks/requests/bank-accounts/use-transaction-badges';
import { appRoutes } from 'app/routes';
import type { SubCategory } from 'app/types/sub-category';
import type { Transaction } from 'app/types/transaction';
import BigNumber from 'bignumber.js';
import { omit } from 'lodash';
import moment from 'moment';
import qs from 'qs';
import React from 'react';
import type { TFunction } from 'react-i18next';
import { ExtraActions } from './extra-actions';
import { RecalculateBalancesAction } from './recalculate-balances-action';
import { ShareMovementsAction } from './share-movements-action';
import { formFields } from './form-config';
import { NoteLinksPanel } from 'app/components/pages/notes/note-links-panel';
import { LinkProjectAction } from './link-project-action';
import { PoolAllocationAction } from './pool-allocation-action';
import { PoolLinkBadge } from './pool-link-badge';
import { TransactionBadges } from './badges/transaction-badges';
import { SimilarTransactionsAction } from './similar-transactions-action';
import { TableFooter } from './table-footer';
import { Wrapper } from './wrapper';
import { BulkEditDrawer } from './bulk-edit-drawer';

/**
 * Field width.
 */

const fieldWidth = 230;

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    render: (item: Transaction) => {
      return (
        <div className={'grid w-full grid-cols-[min-content_1fr] [grid-template-areas:"bankName_bankName"_"id_date"]'}>
          <div className={'text-[9px] mr-4 text-muted-foreground [grid-area:id]'}>{item.id}</div>
          <div className={'flex items-center justify-end text-[9px] text-muted-foreground [grid-area:date]'}>
            {!item.bank_date ? '' : moment(item.bank_date).format('DD-MM-YYYY')}
          </div>
          <div className={'font-medium text-primary [grid-area:bankName]'}>{item.account?.name}</div>
        </div>
      );
    }
  },
  {
    title: translate('common.table.columns.date'),
    dataIndex: 'date',
    key: 'date',
    render: item => moment(item.date).format('DD-MMM')
  },
  {
    title: translate('common.table.columns.category'),
    dataIndex: 'subCategory.name',
    key: 'category',
    render: (item: Transaction) => {
      if (!item.subCategory) {
        return (
          <div className={'rounded-2xl bg-[#cc944a] px-3 py-0 text-xs text-white'}>
            {translate('common.labels.missingCategory')}
          </div>
        );
      }

      return (
        <div>
          <div className={'font-medium text-primary'}>{item.subCategory?.category?.name}</div>
          <div className={'text-xs text-muted-foreground'}>{item.subCategory?.name}</div>
        </div>
      );
    }
  },
  {
    title: translate('common.table.columns.description'),
    dataIndex: 'description',
    size: '1fr',
    mobile: { direction: 'column' },
    key: 'description',
    render: (item: Transaction) => {
      const isSwitch = !!item.ownerSwitchTransaction || item.transactionType === 'switchAccount';
      const isSplit = item.splitTransactions.length > 0 || item.transactionType === 'splitTransaction';
      const isReposition =
        !!item.ownerRepositionTransaction ||
        item.transactionType === 'valueReposition' ||
        item.transactionType === 'bulkValueReposition';
      const projectMovements = item.projectMovements ?? [];
      const hasTags = isSwitch || isSplit || isReposition || projectMovements.length > 0;

      return (
        <div className={'flex min-w-[250px] flex-col gap-1.5 text-xs max-[600px]:w-full max-[600px]:items-center'}>
          <span className={'text-foreground'}>{item.description}</span>

          <PoolLinkBadge transactionId={item.id} />

          {/* One badge row: the automatic tags first, then the user badges and
              the add ("+") control at the end. */}
          <TransactionBadges
            hasLeading={hasTags}
            item={item}
            leading={
              <>
                {isSwitch && (
                  <span
                    className={
                      'inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary'
                    }
                  >
                    {translate('common.labels.switchTransaction')}
                  </span>
                )}

                {isSplit && (
                  <span
                    className={
                      'inline-flex items-center rounded-full bg-[#800000]/10 px-2 py-0.5 text-[11px] font-medium text-[#800000]'
                    }
                  >
                    {translate('common.labels.splitTransaction')}
                  </span>
                )}

                {isReposition && (
                  <span
                    className={
                      'inline-flex items-center rounded-full bg-[#b57edc]/20 px-2 py-0.5 text-[11px] font-medium text-[#7d4fa8]'
                    }
                  >
                    {translate('common.labels.valueReposition')}
                  </span>
                )}

                {projectMovements.map(movement => (
                  <span
                    className={
                      'inline-flex items-center overflow-hidden rounded-md border border-border text-[11px] font-medium'
                    }
                    key={movement.id}
                  >
                    <span className={'bg-muted px-2 py-0.5 text-foreground'}>{movement.description}</span>

                    <span className={'border-l border-border bg-[#eef2fb] px-2 py-0.5 text-[#2f5c9f]'}>
                      {movement.project.name}
                    </span>

                    <span className={'border-l border-border bg-[#eaf6ea] px-2 py-0.5 text-[#2d8d2c]'}>
                      {movement.budget.name}
                    </span>

                    <span className={'border-l border-border bg-[#f6f6e7] px-2 py-0.5 text-[#6f6f1a]'}>
                      {movement.paymentType.name}
                    </span>
                  </span>
                ))}
              </>
            }
          />
        </div>
      );
    }
  },
  {
    title: translate('common.table.columns.previousBalance'),
    dataIndex: 'previousBalance',
    style: { justifyContent: 'flex-end' },
    styleRow: { fontFamily: 'Oswald' },
    key: 'previousBalance',
    render: (item: Transaction) => formatCurrency(item.previousBalance)
  },
  {
    title: translate('common.table.columns.amount'),
    dataIndex: 'amount',
    style: { justifyContent: 'flex-end', minWidth: '32px' },
    styleRow: { fontFamily: 'Oswald' },
    key: 'amount',
    render: (item: Transaction) => {
      const isSplitTransaction = item.splitTransactions.length > 0 || item.transactionType === 'splitTransaction';

      return (
        <span style={!isSplitTransaction ? {} : { textDecoration: 'line-through' }}>{formatCurrency(item.amount)}</span>
      );
    }
  },

  {
    title: translate('common.table.columns.balance'),
    dataIndex: 'balance',
    key: 'balance',
    style: { justifyContent: 'flex-end' },
    styleRow: { fontFamily: 'Oswald' },
    render: (item: Transaction) => formatCurrency(item.balance)
  }
];

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType => ({
  formFields: formFields(translate, undefined),
  displayMode: 'drawer',
  drawerWidth: 780,
  drawerInitialValues: ({ searchParams }) => {
    const account = JSON.parse(searchParams.get('filter') ?? '{}')?.account;

    return account ? { accountId: account } : {};
  },
  acceptQueryFields: [
    {
      name: 'accountId',
      parser: value => {
        return value ? parseInt(value, 10) : undefined;
      }
    }
  ],
  normalizePayload: data => ({
    ...omit(data, ['categoryId', 'balance', 'projectMovements']),
    amount: new BigNumber(data.amount).toString(),
    date: data.date.format('YYYY-MM-DD HH:mm:ss')
  })
});

/**
 * Normalize edit initial values.
 */

function normalizeEditInitialValues(formKeys: any, data: any) {
  const result = {
    ...data,
    targetTransactionId: data?.switchTransaction?.id,
    bank_date: data?.bank_date ? moment(data.bank_date) : undefined,
    date: data?.date ? moment(data.date) : undefined,
    categoryId: data?.subCategory?.categoryId
  };

  if (data?.transactionType === 'switchAccount') {
    result.targetAccountId = data.switchTransaction?.accountId;
  }

  if (data?.transactionType === 'valueReposition') {
    result.targetTransactionId = data.repositionTransaction?.id;
  }

  if (data?.transactionType === 'bulkValueReposition') {
    result.targetTransactionIds = (data.repositionTransactions ?? []).map((item: any) => item.id);
  }

  if (result.splitTransactions?.length > 0) {
    result.splitTransactions = result.splitTransactions.map((item: any) => {
      return normalizeEditInitialValues(formKeys, item);
    });

    result.transactionType = 'splitTransaction';
  }

  return result;
}

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType => ({
  displayMode: 'drawer',
  drawerWidth: 780,
  formFields: (data: any) => {
    if (data?.ownerSwitchTransaction) {
      data.transactionType = 'normal';
    }

    return [
      ...formFields(translate, data),
      { type: 'render', name: 'notes', render: <NoteLinksPanel entityId={data?.id} entityType={'movement'} /> }
    ];
  },
  normalizeInitialValues: normalizeEditInitialValues,
  normalizePayload: data => {
    if (data.transactionType === 'normal' && data?.ownerSwitchTransaction) {
      data.transactionType = 'switchAccount';
    }

    return {
      ...omit(data, [
        'userId',
        'coin',
        'projectMovements',
        'switchTransaction',
        'ownerSwitchTransaction',
        'repositionTransaction',
        'repositionTransactions',
        'ownerRepositionTransaction',
        'balance',
        'statusSplit',
        'id',
        'subCategory',
        'account',
        'badges',
        'previousBalance',
        'categoryId',
        'bank_id',
        'source',
        'bank_date',
        'bank_description',
        'hash'
      ]),
      amount: new BigNumber(data.amount).toString(),
      date: data.date.format('YYYY-MM-DD HH:mm:ss')
    };
  }
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType => ({});

/**
 * List filters.
 */

const listFilters = (translate: TFunction, badgesEnabled = false): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'category',
    style: {
      minWidth: fieldWidth
    },
    label: translate('common.labels.categories'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useCrudRequest as any,
      hookProps: (values: any, params: any) => {
        return [
          {
            key: ['subCategories'],
            options: {
              interpolations: params
            },
            endpoint: apiEndpoints.subCategories
          }
        ];
      },
      normalize: (items: SubCategory[]) => [
        {
          label: translate('common.labels.empty'),
          value: -1
        },
        ...(items?.map(item => ({
          value: item.id,
          label: `${item.category.name}: ${item.name}`
        })) ?? [])
      ]
    }
  },
  {
    type: 'selectSearch',
    name: 'year',
    style: {
      minWidth: fieldWidth
    },
    label: translate('common.labels.year'),
    showSearch: true,
    onChange: (value: any, { resetField }) => {
      if (value) {
        return;
      }

      resetField('month');
    },
    allowClear: true,
    options: {
      hook: useCrudRequest as any,
      hookProps: () => [
        {
          key: 'transactions-year',
          endpoint: apiEndpoints.transactionsFiltersYear
        }
      ],
      normalize: (items: string[]) => {
        return (
          items?.map(item => ({
            value: item,
            label: item
          })) ?? []
        );
      }
    }
  },
  {
    type: 'selectSearch',
    name: 'month',
    style: {
      minWidth: fieldWidth
    },
    disabled: (values: any) => !values.year,
    label: translate('common.labels.month'),
    showSearch: true,
    allowClear: true,
    options: {
      hook: useCrudRequest as any,
      hookProps: (values: any) => {
        return [
          {
            params: {
              year: values?.year
            },
            key: 'transactions-month',
            endpoint: apiEndpoints.transactionsFiltersMonth
          }
        ];
      },
      normalize: (items: string[]) => {
        return (
          items?.map(item => ({
            value: item,
            label: moment()
              .month(parseInt(item, 10) - 1)
              .format('MMMM')
          })) ?? []
        );
      }
    }
  },
  {
    type: 'selectSearch',
    name: 'orderBy',
    style: {
      minWidth: fieldWidth
    },
    label: translate('common.labels.orderBy'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.order.dateAsc'),
        value: 'bank_date#asc'
      },
      {
        label: translate('common.order.dateDesc'),
        value: 'bank_date#desc'
      }
    ]
  },
  {
    type: 'selectSearch',
    name: 'movementType',
    style: {
      minWidth: fieldWidth
    },
    label: translate('common.labels.movementType'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.movement.incomes'),
        value: 'incomes'
      },
      {
        label: translate('common.movement.expenses'),
        value: 'expenses'
      }
    ]
  },
  {
    type: 'selectSearch',
    name: 'transactionType',
    style: {
      minWidth: fieldWidth
    },
    label: translate('common.labels.transactionType'),
    showSearch: true,
    allowClear: true,
    options: [
      {
        label: translate('common.labels.normal'),
        value: 'normal'
      },
      {
        label: translate('common.labels.splitTransaction'),
        value: 'splitTransaction'
      },
      {
        label: translate('common.labels.switchTransaction'),
        value: 'switchAccount'
      },
      {
        label: translate('common.labels.valueReposition'),
        value: 'valueReposition'
      }
    ]
  },
  // Badge filter — only when the feature is on (and thus the user may have badges).
  ...(badgesEnabled
    ? [
        {
          type: 'selectSearch' as const,
          name: 'badgeId',
          style: {
            minWidth: fieldWidth
          },
          label: translate('bankAccounts.movements.badges.filterLabel'),
          showSearch: true,
          allowClear: true,
          options: {
            hook: useTransactionBadges as any,
            normalize: (items: Array<{ id: number; name: string }>) =>
              items?.map(item => ({ value: item.id, label: item.name })) ?? []
          }
        }
      ]
    : [])
];

/**
 * Interpolate add link.
 */

function interpolateAddLink(template: CrudTemplate, searchParams: URLSearchParams) {
  const resolvedRoute = [
    template.add?.route,
    '?',
    qs.stringify({
      accountId: JSON.parse(searchParams.get('filter') as any)?.account
    })
  ].join('');

  return resolvedRoute;
}

/**
 * List.
 */

const list = (translate: TFunction, badgesEnabled = false): CrudListType => ({
  WrapperComponent: Wrapper,
  columns: listColumns(translate),
  route: appRoutes.dashboard.bankAccounts.movementsManager.base,
  key: ['transactions'],
  endpoint: apiEndpoints.transactions,
  interpolateAddLink,
  renderActions: (item: any, template: CrudTemplate, refetch: any) => (
    <>
      <SimilarTransactionsAction item={item} refetch={refetch} template={template} />
      <LinkProjectAction item={item} refetch={refetch} />
      <PoolAllocationAction item={item} refetch={refetch} />
    </>
  ),
  bulkEdit: {
    renderDrawer: ({ items, onClose, onDone }) => <BulkEditDrawer items={items} onClose={onClose} onDone={onDone} />
  },
  canRemove: (values: any) => {
    // Allow remove syncked transactions.
    return (
      !values.bank_date &&
      values.transactionType === 'normal' &&
      !values.ownerSwitchTransaction &&
      !values.ownerRepositionTransaction
    );
  },
  filters: listFilters(translate, badgesEnabled),
  defaultQueryString: 'filter=%7B"orderBy"%3A"bank_date%23desc"%7D&filterOpened=true',
  extraActions: props => (
    <>
      <ExtraActions {...props} />

      <RecalculateBalancesAction {...props} />

      <ShareMovementsAction />
    </>
  ),
  normalizeFilters: (data: any) => {
    const { orderBy, ...filters } = data.filters;
    const result: any = { filters, order: [] };

    if (filters?.category === -1) {
      filters.category = null;
    }

    // Always sort by booking date by default (id is only a tie-breaker). Without
    // this, navigating without an explicit orderBy fell back to id order — which
    // diverges from date when transactions are imported out of chronological
    // order (e.g. Coverflex's recent-then-full-history sync).
    const effectiveOrderBy = orderBy ?? 'bank_date#desc';
    const parts = effectiveOrderBy.split('#');

    result.order.push({ key: parts[0], direction: parts[1] });
    result.order.push({ key: 'id', direction: 'desc' });

    return result;
  },
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  renderLastLine: props => <TableFooter {...props} />
});

/**
 * Config.
 */

export function createTransactionsTemplate(translate: TFunction, badgesEnabled = false): CrudTemplate {
  return normalizeTemplate(translate, {
    list: list(translate, badgesEnabled),
    add: add(translate),
    edit: edit(translate),
    remove: remove()
  });
}
